import {Constants} from '../constants';

/**
 * How a notification row reads — shared by the notification panel
 * (`notification-feed`) and the "View all notifications" page, so a row gets the
 * same type, title, chip, photo and destination on both.
 *
 * A row is the PHP `notification` list's shape (`fromUser`, `note.quote`,
 * `noteReaction.note`, `quoteProduct.quote`); api-v2's `notification/feed`
 * returns the same shape.
 */

/** Row accent type: the rail, avatar tint and badge icon. */
export type NotificationRowType = 'order' | 'quote' | 'sample' | 'mention' | 'system';

/** What the chip names: the linked record's kind, or `mention` for a person's
 *  notification (its colour comes from this too). */
export type NotificationChipKind = 'order' | 'quote' | 'sample' | 'account' | 'contact' | 'mention';

/** The chip under the message: "Order · O-WEB-4359", "Mention · OH Architecture". */
export interface NotificationChip {
  kind: NotificationChipKind;
  label: string;
  ref: string;
}

/** Where opening a row goes: router commands plus query params. */
export interface NotificationTarget {
  commands: any[];
  queryParams: Record<string, any>;
}

/** The note a notification hangs off — a "liked your note" row's is its reaction's. */
export function linkedNote(notification: any): any {
  const src = notification?.note_reaction_id ? notification?.noteReaction : notification;
  return src?.note || null;
}

/** The quote/order/sample a notification hangs off, following the reaction/
 *  note/quote-product links. Null for account/contact notes. */
export function linkedQuote(notification: any): any {
  const src = notification?.note_reaction_id ? notification?.noteReaction : notification;
  return src?.note?.quote || src?.quoteProduct?.quote || null;
}

/** An account / contact note's record, the way the row opens it: the account
 *  when the note has one, otherwise the contact. Null for a note on a quote,
 *  order or sample, or on Pebbl feedback. */
function linkedParty(notification: any): { kind: 'account' | 'contact'; note: any } | null {
  const note = linkedNote(notification);
  if (!note || note.quote_id || note.query_cost_log_id) {
    return null;
  }
  if (note.company_id) {
    return {kind: 'account', note};
  }
  return note.frontend_user_id ? {kind: 'contact', note} : null;
}

/** An account / contact note whose name is not on the row yet. The PHP list the
 *  panel reads has no names; api-v2's `notification/record-names` fills them in. */
export function needsRecordName(notification: any): boolean {
  const party = linkedParty(notification);
  return !!party && (party.kind === 'account' ? !party.note.company : !party.note.frontendUser);
}

/**
 * Who sent it decides the type. A person's notification (a mention, a reply, a
 * like, a product comment) is a Mention; the console's own (no sender) takes its
 * record's type — Order, Quote or Sample — or System when it has none.
 */
export function notificationType(notification: any): NotificationRowType {
  if (notification?.from_user_id) {
    return 'mention';
  }
  switch (linkedQuote(notification)?.type) {
    case Constants.ORDER_TYPE_QUOTE_SHORT: return 'quote';
    case Constants.ORDER_TYPE_SAMPLE_SHORT: return 'sample';
    case Constants.ORDER_TYPE_ORDER_SHORT: return 'order';
    default: return 'system';
  }
}

export function notificationTypeLabel(type: string): string {
  switch (type) {
    case 'order': return 'Order';
    case 'quote': return 'Quote';
    case 'sample': return 'Sample';
    case 'account': return 'Account';
    case 'contact': return 'Contact';
    case 'mention': return 'Mention';
    default: return 'System';
  }
}

/**
 * The chip: the linked record — a quote / order / sample number, an account's
 * company name or a contact's full name — labelled with its kind ("Order ·
 * O-WEB-4359", "Account · OH Architecture"), or "Mention · …" when a person sent
 * it. Null when there is no record to name.
 */
export function notificationChip(notification: any): NotificationChip | null {
  let kind: NotificationChipKind;
  let ref = '';
  const quote = linkedQuote(notification);
  if (quote) {
    switch (quote.type) {
      case Constants.ORDER_TYPE_QUOTE_SHORT: kind = 'quote'; ref = quote.quote_number || ''; break;
      case Constants.ORDER_TYPE_SAMPLE_SHORT: kind = 'sample'; ref = quote.order_number || ''; break;
      case Constants.ORDER_TYPE_ORDER_SHORT: kind = 'order'; ref = quote.order_number || ''; break;
      default: return null;
    }
  } else {
    const party = linkedParty(notification);
    if (!party) {
      return null;
    }
    kind = party.kind;
    ref = party.kind === 'account'
      ? (party.note.company?.company_name || '')
      : [party.note.frontendUser?.first_name, party.note.frontendUser?.last_name].filter(Boolean).join(' ');
  }
  ref = ref.trim();
  if (!ref) {
    return null;
  }
  if (notification?.from_user_id) {
    kind = 'mention';
  }
  return {kind, label: notificationTypeLabel(kind), ref};
}

export function notificationSenderName(fromUser: any): string {
  if (fromUser) {
    const firstName = fromUser.first_name ? fromUser.first_name : '';
    const lastName = fromUser.last_name ? fromUser.last_name : '';
    return `${firstName} ${lastName}`;
  }
  return 'GatherCo System';
}

/** Bold headline: "{sender} {action}". */
export function notificationTitle(notification: any): string {
  const sender = notificationSenderName(notification?.fromUser);
  let action = '';
  if (notification?.quote_product_id) {
    action = 'mentioned you in a comment';
  } else if (notification?.noteReaction?.id) {
    action = 'liked your note';
  } else if (notification?.parent_note_id) {
    action = 'replied to your note';
  } else if (notification?.note_id) {
    action = 'mentioned you in a note';
  }
  return action ? `${sender} ${action}` : sender;
}

/** The full note/comment body, or the raw message. May be HTML. */
export function notificationBody(notification: any): string {
  return notification?.noteReaction?.id
    ? (notification?.noteReaction?.note?.note || '')
    : (notification?.message || '');
}

/** The sender's photo, or the GatherCo mark for a system notification. */
export function notificationUserImage(profile_picture: any): string {
  const getKitPath = Constants.S3_PRODUCT_IMAGES_IMAGE_KIT_URL;
  if (profile_picture != '' && profile_picture != null) {
    return getKitPath + '/USER_PROFILE_PICTURE/' + profile_picture + Constants.IMAGE_SIZE_50_X_50;
  }
  return getKitPath + '/WEBSITE_ASSET_STAGING/gatherco_icon.png' + Constants.IMAGE_SIZE_50_X_50;
}

/** The note the thumb acts on — the note itself, or the note behind a "liked
 *  your note" reaction notification. Null for product-comment notifications,
 *  which have no note to react to. */
export function likeTargetNote(notification: any): any {
  return notification?.note_reaction_id ? (notification?.noteReaction?.note || null) : (notification?.note || null);
}

/* Every route below starts with "/". The View all page puts these commands in a
   `[routerLink]`, which resolves a relative first segment against the page it is
   on (`/notifications/sample-requests/view/…`); the panel's `router.navigate`
   resolves from the root either way. */

function recordRoute(type: string): string {
  switch (type) {
    case Constants.ORDER_TYPE_SAMPLE_SHORT:
      return '/sample-requests/view/';
    case Constants.ORDER_TYPE_QUOTE_SHORT:
      return '/quotes/view/';
    case Constants.ORDER_TYPE_ORDER_SHORT:
      return '/orders/view/';
    default:
      return '';
  }
}

/**
 * Where opening a notification goes, or null when there is nowhere to go.
 * Pass the reaction (`noteReaction`) for a "liked your note" row — its note is
 * the one to open.
 */
export function notificationTarget(notification: any): NotificationTarget | null {
  if (notification?.note_id) {
    const note = notification.note;
    if (!note) {
      return null;
    }
    if (note.query_cost_log_id) {
      return {commands: ['/pebbl-feedback/view/', note.query_cost_log_id], queryParams: {}};
    }
    const type = note.quote ? note.quote.type : null;
    if (type) {
      const route = recordRoute(type);
      return route ? {commands: [route, note.quote_id], queryParams: {note_id: notification.note_id}} : null;
    }
    // Account and contact notes live on their own Internal Notes tab; the
    // thread's top note is what that tab can scroll to.
    const queryParams = {tab: 'internal-notes', note_id: note.parent_note_id ?? note.id};
    return note.company_id !== null
      ? {commands: ['/accounts/', note.company_id], queryParams}
      : {commands: ['/contacts/', note.frontend_user_id], queryParams};
  }

  if (notification?.quote_product_id) {
    if (!notification.message) {
      return null;
    }
    const quoteProduct = notification.quoteProduct;
    const type = quoteProduct?.quote ? quoteProduct.quote.type : null;
    const route = type ? recordRoute(type) : '';
    return route ? {commands: [route, quoteProduct.quote_id], queryParams: {product_id: notification.quote_product_id}} : null;
  }
  return null;
}

/**
 * The message HTML as a row shows it, on the panel and the View all page:
 * - a run of two or more "Label: value" paragraphs (`<p><strong>Label: </strong>value</p>`,
 *   as payment and trade-application notes are written) is drawn as the design's
 *   bordered detail box (`.details` / `.detail` / `.d-label`), without the blank
 *   paragraph that spaced it from the sentence above;
 * - `query`'s hits are marked in the text, except inside the detail labels.
 * The result still goes through Angular's [innerHTML] sanitising.
 */
export function notificationMessageHtml(html: string, query: string = ''): string {
  if (!html || typeof DOMParser === 'undefined') {
    return html || '';
  }
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild as HTMLElement;

  const isDetailLine = (el: Element | null): boolean => {
    if (!el || el.tagName !== 'P') {
      return false;
    }
    const first = el.firstElementChild;
    return !!first && first.tagName === 'STRONG' && el.firstChild === first && /:\s*$/.test(first.textContent || '');
  };
  const isBlank = (el: Element | null): boolean => !!el && el.tagName === 'P' && !(el.textContent || '').trim();

  const children = Array.from(root.children);
  for (let i = 0; i < children.length; i++) {
    if (!isDetailLine(children[i])) {
      continue;
    }
    let end = i;
    while (end + 1 < children.length && isDetailLine(children[end + 1])) {
      end++;
    }
    if (end > i) {
      const box = doc.createElement('div');
      box.className = 'details';
      const previous = children[i].previousElementSibling;
      if (isBlank(previous)) {
        previous!.remove();
      }
      children[i].before(box);
      for (let j = i; j <= end; j++) {
        const line = children[j];
        line.className = 'detail';
        const label = line.firstElementChild!;
        const b = doc.createElement('b');
        b.className = 'd-label';
        b.innerHTML = label.innerHTML;
        label.replaceWith(b);
        box.appendChild(line);
      }
    }
    i = end;
  }

  if (query) {
    markHits(root, query.toLowerCase());
  }
  return root.innerHTML;
}

/** Wraps every case-insensitive occurrence of `query` in a <mark>, skipping the detail labels. */
function markHits(root: HTMLElement, query: string): void {
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!(node.parentElement?.closest('.d-label'))) {
      nodes.push(node as Text);
    }
  }
  for (const node of nodes) {
    const text = node.data;
    const low = text.toLowerCase();
    if (!low.includes(query)) {
      continue;
    }
    const fragment = doc.createDocumentFragment();
    let at = 0;
    for (let i = low.indexOf(query); i >= 0; i = low.indexOf(query, at)) {
      if (i > at) {
        fragment.appendChild(doc.createTextNode(text.slice(at, i)));
      }
      const mark = doc.createElement('mark');
      mark.textContent = text.slice(i, i + query.length);
      fragment.appendChild(mark);
      at = i + query.length;
    }
    if (at < text.length) {
      fragment.appendChild(doc.createTextNode(text.slice(at)));
    }
    node.replaceWith(fragment);
  }
}

/** A person picked in the "from" menu: the panel's sender menu or the page's From filter. */
export interface NotificationSender {
  id: number;
  name: string;
  picture: string | null;
  initials: string;
}

/** Where the picked person is kept — like "Only show unread" (`notif_only_unread`),
 *  one key shared by the panel and the View all notifications page. */
const SENDER_KEY = 'notif_from_user';

/**
 * The person last picked in the panel or on the page, so reopening either keeps
 * the choice; null is Everyone. Saved with the reader's id, so another admin who
 * signs in at the same browser starts on Everyone. The name and photo come back
 * with it, so the menu button reads right before the staff list has loaded.
 */
export function rememberedSender(readerId: any): NotificationSender | null {
  try {
    const saved = JSON.parse(localStorage.getItem(SENDER_KEY) || 'null');
    const person = saved?.person;
    if (!person || Number(saved.reader) !== Number(readerId) || !(Number(person.id) > 0)) {
      return null;
    }
    return {id: Number(person.id), name: String(person.name ?? ''), picture: person.picture ?? null, initials: String(person.initials ?? '')};
  } catch {
    return null;
  }
}

/** Saves the picked person for {@link rememberedSender}; null (Everyone) forgets it. */
export function rememberSender(readerId: any, person: NotificationSender | null): void {
  try {
    if (person) {
      const {id, name, picture, initials} = person;
      localStorage.setItem(SENDER_KEY, JSON.stringify({reader: Number(readerId), person: {id: Number(id), name, picture, initials}}));
    } else {
      localStorage.removeItem(SENDER_KEY);
    }
  } catch {
    // No storage: the pick still applies, it just won't be remembered.
  }
}
